import { I18N_CACHE_KEY_PREFIX } from "@/constants/common";

export const DEFAULT_LANGUAGE = "en";

export const normalizeLanguageCode = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const normalizedValue = value.trim().toLowerCase();
  if (!normalizedValue) {
    return undefined;
  }

  const baseLanguage = normalizedValue.split(/[-_]/)[0];
  return /^[a-z]{2,3}$/.test(baseLanguage) ? baseLanguage : undefined;
};

export const normalizeSupportedLanguageCodes = (codes: string[]): string[] => {
  const uniqueCodes = new Set<string>();

  codes.forEach((code) => {
    const normalizedCode = normalizeLanguageCode(code);
    if (!normalizedCode) {
      return;
    }

    uniqueCodes.add(normalizedCode);
  });

  uniqueCodes.add(DEFAULT_LANGUAGE);

  return Array.from(uniqueCodes);
};

export const getSupportedLanguageFromPathname = (pathname?: string) => {
  if (!pathname) {
    return undefined;
  }

  const firstSegment = pathname.split("/").filter(Boolean)[0];
  return normalizeLanguageCode(firstSegment);
};

// ---------------------------------------------------------------------------
// Locale URL registry — stores S3 presigned URLs from the /locales API
// ---------------------------------------------------------------------------

const localeUrlMap: Record<string, string> = {};

export const registerLocaleUrls = (
  locales: Array<{ name: string; url: string }>,
): void => {
  locales.forEach(({ name, url }) => {
    localeUrlMap[name] = url;
  });
};

export const getLocaleUrl = (language: string): string | undefined =>
  localeUrlMap[language];

// Callback registered by ProtectedRoute to re-fetch /locales and refresh URLs
let localeUrlRefresher: (() => Promise<void>) | null = null;

export const setLocaleUrlRefresher = (refresher: () => Promise<void>): void => {
  localeUrlRefresher = refresher;
};

export const getLocaleUrlRefresher = (): (() => Promise<void>) | null =>
  localeUrlRefresher;

// ---------------------------------------------------------------------------
// Session-storage translation cache helpers
// ---------------------------------------------------------------------------

export const getSessionCache = (language: string): unknown | null => {
  try {
    const cached = sessionStorage.getItem(
      `${I18N_CACHE_KEY_PREFIX}${language}`,
    );
    return cached ? (JSON.parse(cached) as unknown) : null;
  } catch {
    return null;
  }
};

export const setSessionCache = (
  language: string,
  translation: unknown,
): void => {
  try {
    sessionStorage.setItem(
      `${I18N_CACHE_KEY_PREFIX}${language}`,
      JSON.stringify(translation),
    );
  } catch {
    // sessionStorage unavailable or full — silently skip caching
  }
};

export const clearSessionCache = (language: string): void => {
  try {
    sessionStorage.removeItem(`${I18N_CACHE_KEY_PREFIX}${language}`);
  } catch {
    // sessionStorage unavailable — silently skip
  }
};
